import React from 'react'
import { useSelector } from 'react-redux'

import styled from 'styled-components'
import { Tooltip } from 'antd'
import { colours } from '@qflow/theme'

import { AI, Person } from 'portal/lib/primitives/icons'

import {
  transformationMessages,
  getCustomTransformationMessages,
  ApiClientSourceTrackingTransformationTypes
} from './valueAddHelpers'

interface ValueAddObject {
  fieldName: string | null
  value: string | number | null
  type: 'Transformation' | 'Client' | null
  transformationType: ApiClientSourceTrackingTransformationTypes | null
  clientUserId: string | null
  clientTimestamp: string | null
}
interface ValueAddProp {
  details: ValueAddObject | null
}

export function ValueAdd({ details = null }: ValueAddProp) {
  const {
    fieldName,
    value,
    type,
    transformationType,
    clientUserId,
    clientTimestamp
  } = details || {}

  const projectTimeZone = useSelector(
    (state: any) => state.projectDetails.modules?.timeZone
  )
  const projectUsers = useSelector((state: any) => state.projectUsers)

  type User = {
    userId: string
    displayName: string
  }

  const users: User[] = Object.values(projectUsers.users)

  const getTooltipTitle = () => {
    if (fieldName && transformationType) {
      const customMessage = getCustomTransformationMessages(
        fieldName,
        transformationType
      )
      if (customMessage) {
        return customMessage
      }
    }

    if (transformationType && transformationMessages[transformationType]) {
      return transformationMessages[transformationType]
    }

    if (clientUserId && clientTimestamp) {
      const user = users.find(u => u.userId === clientUserId)
      return (
        <span>
          {user?.displayName ? (
            <>
              {user.displayName}
              <br />
            </>
          ) : (
            ''
          )}
          {clientTimestamp
            ? new Date(clientTimestamp).toLocaleString([], {
                timeZone: projectTimeZone,
                year: 'numeric',
                month: '2-digit',
                day: '2-digit'
              })
            : ''}
        </span>
      )
    }

    return null
  }

  const hasValidValue =
    (Array.isArray(value) && value.length > 0) ||
    (typeof value === 'number' && !isNaN(value)) ||
    (typeof value === 'string' && value.trim() !== '')

  if (!type || (type === 'Transformation' && !hasValidValue)) {
    return null
  }

  return (
    <Tooltip title={getTooltipTitle()} placement="left">
      <IconWrapper className="ValueAdd__IconWrapper">
        {type === 'Client' && <Person />}
        {type === 'Transformation' && <AI />}
      </IconWrapper>
    </Tooltip>
  )
}

const IconWrapper = styled.div`
  color: ${colours.CORE_NAVY};
  display: flex;
  width: 25px;
  height: 25px;
  border-radius: 50%;
  justify-content: center;
  align-items: center;

  &:hover {
    color: ${colours.TEAL};
    background-color: ${colours.WHITE_10};
  }
`
